import { CommonModule } from '@angular/common';
import { Component, inject, input, signal, type InputSignal, type WritableSignal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { AutoCompleteModule, type AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { CardHeader } from '../../../../../shared/components/card/card-header/card-header';
import { Card } from '../../../../../shared/components/card/card/card';
import { EmptyChart } from '../../../../../shared/components/empty-chart/empty-chart';
import { ReportCohortPeriodPipe } from '../../../../../shared/pipes/report-cohort-period-pipe';
import dayjs from 'dayjs';
import { finalize } from 'rxjs';
import { ReportCohortBasedOn, ReportCohortPeriod } from '../../../../../interfaces/reports';
import { ReportsService } from '../../../../../services/reports.service';
import { colorByPercentage } from '../../../../../shared/functions/color-by-percentage';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { SelectModule } from 'primeng/select';
import { ReportCohortBasedByPipe } from '../../../../../shared/pipes/report-cohort-based-by-pipe';
import { MenuModule } from 'primeng/menu';
import type { MenuItem } from 'primeng/api';
import saveAs from 'file-saver';

@Component({
  selector: 'app-cohort-deposits',
  imports: [
    CommonModule,
    FormsModule,
    Card,
    CardHeader,
    TableModule,
    SkeletonModule,
    ProgressSpinnerModule,
    DatePickerModule,
    ReactiveFormsModule,
    FloatLabelModule,
    ButtonModule,
    AutoCompleteModule,
    SelectButtonModule,
    ReportCohortPeriodPipe,
    TooltipModule,
    EmptyChart,
    SelectModule,
    ReportCohortBasedByPipe,
    MenuModule
  ],
  templateUrl: './cohort-deposits.html',
  styleUrl: './cohort-deposits.css',
})
export class CohortDeposits {

  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _reportsService: ReportsService = inject(ReportsService);

  public exportLoading: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly reportCohortPeriod: typeof ReportCohortPeriod = ReportCohortPeriod;
  protected readonly reportCohortBasedOn: typeof ReportCohortBasedOn = ReportCohortBasedOn;

  public financialBrandUtmSourceData: InputSignal<any> = input<any>(null);

  public financialCohortDepositsShow: ReportCohortPeriod = ReportCohortPeriod.DAILY;

  public financialCohortDepositsUtmFilter: WritableSignal<string[]> = signal<string[]>([]);
  public financialCohortDepositsForm: FormGroup = this._fb.group({
    utm_source: [null],
    based_by: [ReportCohortBasedOn.CREATED],
    date_range_daily: [[dayjs().startOf('month').toDate(), dayjs().toDate()]],
    date_range_monthly: [[dayjs().startOf('year').toDate(), dayjs().toDate()]],
  });

  public financialCohortDepositsDailyData: WritableSignal<any> = signal<any>(null);
  public financialCohortDepositsDailyFirstRequest: WritableSignal<boolean> = signal<boolean>(true);
  public financialCohortDepositsDailyLoading: WritableSignal<boolean> = signal<boolean>(false);

  public financialCohortDepositsMonthlyData: WritableSignal<any> = signal<any>(null);
  public financialCohortDepositsMonthlyFirstRequest: WritableSignal<boolean> = signal<boolean>(true);
  public financialCohortDepositsMonthlyLoading: WritableSignal<boolean> = signal<boolean>(false);

  public exportMenuItems: MenuItem[] = [
    {
      label: 'PDF',
      icon: 'fa-solid fa-file-pdf',
      action: () => this.export()
    },
    {
      label: 'CSV',
      icon: 'fa-solid fa-file-csv',
      action: () => this.exportCsv()
    }
  ];

  get tableId(): string {
    if (this.financialCohortDepositsShow === ReportCohortPeriod.DAILY) {
      return 'financialCohortDepositsDailyTable';
    }
    return 'financialCohortDepositsMonthlyTable';
  }

  public filterUtmMethodCompleteMethod(event: AutoCompleteCompleteEvent): void {
    this.financialCohortDepositsUtmFilter.set(
      this.financialBrandUtmSourceData().utm_sources.filter((item: string) => item.toLowerCase().includes(event.query.toLowerCase()))
    )
  }

  public fromPaymentFiltersForm(form: FormGroup, key: string): string {

    if (key === 'start_date_daily') {
      return dayjs(form.get('date_range_daily')?.value[0]).format('YYYY-MM-DD');
    }

    if (key === 'end_date_daily') {
      return dayjs(form.get('date_range_daily')?.value[1]).format('YYYY-MM-DD');
    }

    if (key === 'start_date_monthly') {
      return dayjs(form.get('date_range_monthly')?.value[0]).format('YYYY-MM-DD');
    }

    if (key === 'end_date_monthly') {
      return dayjs(form.get('date_range_monthly')?.value[1]).format('YYYY-MM-DD');
    }

    return form.get(key)?.value;
  }

  public getFinancialCohortDeposits(force: boolean = false): void {

    if (!force) {
      if (this.financialCohortDepositsShow === ReportCohortPeriod.DAILY
        && this.financialCohortDepositsDailyData()) {
        return;
      }

      if (this.financialCohortDepositsShow === ReportCohortPeriod.MONTHLY
        && this.financialCohortDepositsMonthlyData()
      ) {
        return;
      }
    }

    if (this.financialCohortDepositsShow === ReportCohortPeriod.DAILY) {
      this.getFinancialCohortDepositsDaily();
    }

    if (this.financialCohortDepositsShow === ReportCohortPeriod.MONTHLY) {
      this.getFinancialCohortDepositsMonthly();
    }
  }

  public getFinancialCohortDepositsDaily(): void {
    this.financialCohortDepositsDailyLoading.set(true);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'start_date_daily'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'end_date_daily'),
    };

    this.financialCohortDepositsDailyData.set(null);
    this.financialCohortDepositsDailyFirstRequest.set(false);

    this._reportsService.financialCohortDepositsDaily(filters)
      .pipe(finalize(() => this.financialCohortDepositsDailyLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.financialCohortDepositsDailyData.set(response.data);
        }
      })
  }

  public getFinancialCohortDepositsMonthly(): void {
    this.financialCohortDepositsMonthlyLoading.set(true);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'start_date_monthly'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'end_date_monthly'),
    };

    this.financialCohortDepositsMonthlyData.set(null);
    this.financialCohortDepositsMonthlyFirstRequest.set(false);

    this._reportsService.financialCohortDepositsMonthly(filters)
      .pipe(finalize(() => this.financialCohortDepositsMonthlyLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.financialCohortDepositsMonthlyData.set(response.data);
        }
      })
  }

  public color(percentage: number) {
    return colorByPercentage(percentage);
  }

    public exportCsv(): void {
      if (this.financialCohortDepositsShow === ReportCohortPeriod.DAILY) {
        this.exportCsvDaily();
      } else if (this.financialCohortDepositsShow === ReportCohortPeriod.MONTHLY) {
        this.exportCsvMonthly();
      }
    }

    public exportCsvDaily(): void {
      this.exportLoading.update(value => !value);

      const filters = {
        based_by: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'based_by'),
        utm_source: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'utm_source'),
        start_date: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'start_date_daily'),
        end_date: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'end_date_daily'),
      };

      this._reportsService.financialCohortDepositsDailyCsv(filters)
        .pipe(finalize(() => this.exportLoading.update(value => !value)))
        .subscribe({
          next: (blob) => {
            console.log({blob})
            const fileName = `cohort-depositos-diario-${new Date().toLocaleString()}.csv`;
            saveAs(blob, fileName);
          },
        })
    }

    public exportCsvMonthly(): void {
      this.exportLoading.update(value => !value);

      const filters = {
        based_by: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'based_by'),
        utm_source: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'utm_source'),
        start_date: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'start_date_monthly'),
        end_date: this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'end_date_monthly'),
      };

      this._reportsService.financialCohortDepositsMonthlyCsv(filters)
        .pipe(finalize(() => this.exportLoading.update(value => !value)))
        .subscribe({
          next: (blob) => {
            console.log({blob})
            const fileName = `cohort-depositos-mensal-${new Date().toLocaleString()}.csv`;
            saveAs(blob, fileName);
          },
        })
    }

  public export() {
    this.exportLoading.set(true);
    const el = document.getElementById(this.tableId);

    const period = {
      start: this.financialCohortDepositsShow === ReportCohortPeriod.DAILY ?
        this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'start_date_daily') :
        this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'start_date_monthly'),
      end: this.financialCohortDepositsShow === ReportCohortPeriod.DAILY ?
        this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'end_date_daily') :
        this.fromPaymentFiltersForm(this.financialCohortDepositsForm, 'end_date_monthly'),
    }

    if (el) {
      setTimeout(async () => {
        html2canvas(el).then(canvas => {
          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF('p');
          const imgWidth = 200;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          pdf.setFontSize(12);
          pdf.text("Cohort - Depósitos", 5, 10);
          pdf.text(`${dayjs(period.start).format('DD/MM/YYYY')} - ${dayjs(period.end).format('DD/MM/YYYY')}`, 205, 10, { align: 'right' });
          pdf.addImage(imgData, 'PNG', 5, 15, imgWidth, imgHeight);
          pdf.save(`${this.tableId}-${dayjs().format('YYYY-MM-DD-HH:mm:ss')}.pdf`);

          this.exportLoading.set(false);
        });
      });
    }

  }
}
